import { useCallback, useMemo, useState } from 'react'
import { useClearRefinements, useRefinementList } from 'react-instantsearch'
import type { ActionMeta, InputActionMeta, MultiValue } from 'react-select'
import type { RefinementListItem } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList'

import { Label } from 'Components/_theme'
import { Select } from 'Components/Select/Select'

import { scrollToTop } from 'Utilities'

import { useCurrentRefinement } from 'Algolia/hooks/useCurrentRefinement/useCurrentRefinement'

import type { MultiselectFilterProps } from './MultiselectFilter.types'

export const MultiselectFilter = (props: MultiselectFilterProps) => {
  const {
    filterProps,
    isSearchableOnServer = false,
    label,
    selectProps,
    valueMapper,
  } = props
  const { items, refine, searchForItems } = useRefinementList({
    // limits the number of items displayed
    // default is 10
    // amount is also controlled by maxValuesPerFacet in <Configure />
    limit: 100,
    // enforce default sort order
    // otherwise after first selection, the order is not preserved
    sortBy: ['name:asc'],
    ...filterProps,
  })
  const [facetQuery, setFacetQuery] = useState('')
  const { refine: clearRefine } = useClearRefinements({
    includedAttributes: [filterProps.attribute],
  })
  const currentRefinement = useCurrentRefinement(filterProps.attribute)

  const value = useMemo<MultiValue<RefinementListItem>>(() => {
    if (typeof valueMapper === 'function') {
      return currentRefinement.reduce(valueMapper, [])
    }

    return currentRefinement.map(item => ({
      count: item.count ?? 0,
      isRefined: true,
      label:
        typeof filterProps.transformItems === 'function'
          ? (items.find(i => i.value === item.value)?.label ?? item.label)
          : item.label,
      value: item.value.toString(),
    }))
  }, [currentRefinement, filterProps.transformItems, items, valueMapper])

  const filteredItems = useMemo(() => items.filter(item => item.label), [items])

  const handleRefine = useCallback(
    (value: string) => {
      refine(value)
    },
    [refine],
  )

  const handleChange = useCallback(
    (
      _newValue: MultiValue<RefinementListItem>,
      actionMeta: ActionMeta<RefinementListItem>,
    ) => {
      scrollToTop()

      if (actionMeta.action === 'select-option' && actionMeta.option) {
        handleRefine(actionMeta.option.value)

        return
      }

      if (actionMeta.action === 'deselect-option' && actionMeta.option) {
        handleRefine(actionMeta.option.value)

        return
      }

      if (actionMeta.action === 'remove-value' && actionMeta.removedValue) {
        handleRefine(actionMeta.removedValue.value)

        return
      }

      if (actionMeta.action === 'clear') {
        clearRefine()
      }
    },
    [clearRefine, handleRefine],
  )

  const handleInputChange = useCallback(
    (inputValue: string, actionMeta: InputActionMeta) => {
      if (!isSearchableOnServer) {
        return
      }

      // react-select empties the input on blur/close, so the facet search has to be
      // reset as well - otherwise the last query still narrows the options next time
      const query = actionMeta.action === 'input-change' ? inputValue : ''

      setFacetQuery(query)
      searchForItems(query)
    },
    [isSearchableOnServer, searchForItems],
  )

  const iconProps = useMemo(() => {
    if (!selectProps?.iconProps) {
      return {
        iconProps: undefined,
      }
    }

    return {
      iconProps: {
        size: 20,
        ...selectProps.iconProps,
      },
    }
  }, [selectProps?.iconProps])

  // an empty option list means the attribute has no values to filter by, except
  // while a server side search is narrowing them down or a value is already selected
  if (filteredItems.length === 0 && !facetQuery && value.length === 0) {
    return null
  }

  return (
    <div className="Filter-input-container">
      <Label>{label}</Label>

      <Select
        isClearable
        isMulti
        isSearchable
        onChange={handleChange}
        options={filteredItems}
        value={value}
        {...(isSearchableOnServer && {
          // the server already returns the matching values, filtering them again
          // in the browser would only hide results
          filterOption: null,
          onInputChange: handleInputChange,
        })}
        {...selectProps}
        {...iconProps}
      />
    </div>
  )
}
